import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaSave, FaTimes, FaCheckCircle, FaRedo } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import CharacterEditModal from './CharacterEditModal';

export default function CharacterPreviewModal({ 
  isOpen, 
  onClose, 
  character, 
  characterImage, 
  onSaveCharacter, 
  onEditCharacter,
  onRegenerateImage,
  // New props for StorySetupModal integration
  characterData,
  onSave,
  onEdit,
  onRegenerate,
  isGenerating,
  error
}) {
  const [editPrompt, setEditPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Use the appropriate character data
  const charData = character || characterData;
  const charImage = characterImage;
  const handleSave = onSaveCharacter || onSave;
  const handleEdit = onEditCharacter || onEdit;
  const handleRegenerate = onRegenerateImage || onRegenerate;
  const generating = isGenerating || isRegenerating;

  // Construct full image URL if needed
  const fullImageUrl = charImage && charImage.startsWith('/uploads/') 
    ? `${API_ENDPOINTS.backendBase}${charImage}` 
    : charImage;

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleCharacterUpdate = (updatedCharacter) => {
    // Update the character data with the new image
    if (onSaveCharacter) {
      onSaveCharacter(updatedCharacter);
    }
    if (onSave) {
      onSave(updatedCharacter);
    }
    setShowEditModal(false);
  };

  const handleSaveEdit = async () => {
    if (editPrompt.trim()) {
      try {
        await handleEdit(editPrompt);
        setIsEditing(false);
        setEditPrompt('');
      } catch (error) {
        console.error('Error updating character:', error);
        alert('Failed to update character. Please try again.');
      }
    }
  };

  const handleRegenerateClick = async () => {
    setIsRegenerating(true);
    try {
      await handleRegenerate(editPrompt || createCharacterDescription(charData));
    } catch (error) {
      console.error('Error regenerating image:', error);
      alert('Failed to regenerate image. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const createCharacterDescription = (char) => {
    // If we have a detailed character object, use it
    if (char.hairColor || char.clothing) {
      const parts = [
        `A ${char.age} year old ${char.gender}`,
        char.hairColor && char.hairStyle ? `with ${char.hairStyle} ${char.hairColor} hair` : '',
        char.eyeColor ? `and ${char.eyeColor} eyes` : '',
        char.skinTone ? `with ${char.skinTone} skin` : '',
        char.height ? `, ${char.height} height` : '',
        char.build ? `, ${char.build} build` : '',
        char.clothing?.top ? `wearing ${char.clothing.top}` : '',
        char.clothing?.bottom ? `and ${char.clothing.bottom}` : '',
        char.clothing?.colors?.length > 0 ? `in ${char.clothing.colors.join(', ')} colors` : '',
        char.clothing?.accessories?.length > 0 ? `with ${char.clothing.accessories.join(', ')}` : '',
        char.distinctiveFeatures?.length > 0 ? `, ${char.distinctiveFeatures.join(', ')}` : '',
        char.countryOfOrigin ? `, from ${char.countryOfOrigin}` : '',
        char.paintingStyle ? `, ${char.paintingStyle} style` : ', Comforting Old french children book illustration style',
        ', high quality, detailed'
      ].filter(Boolean);
      return parts.join(' ');
    } else {
      // Fallback for simple character data
      return `${char.name}, ${char.age} year old ${char.gender}, ${char.appearance || ''}, ${char.countryOfOrigin ? `from ${char.countryOfOrigin}, ` : ''}Comforting Old french children book illustration style, high quality, detailed`;
    }
  };

  if (!isOpen) return null;
  if (!charData) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Compact header — same aesthetic as story setup (no amber) */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">Character Preview</h2>
          <button onClick={onClose} className="p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100" aria-label="Close">×</button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Character Image — no "Generated Character" label */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                {charImage ? (
                  <>
                    <img
                      src={fullImageUrl}
                      alt={`${charData?.name || 'Character'}`}
                      className="w-full h-80 object-contain rounded-xl border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="h-80 hidden items-center justify-center text-gray-500 bg-gray-100 rounded-xl border border-gray-200" style={{ display: 'none' }}>
                      <p>Failed to load image</p>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 text-center">Comforting Old French Children Book Illustration Style</p>
                  </>
                ) : (
                  <div className="h-80 flex items-center justify-center text-gray-500">
                    <p>No image generated yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Character Details only (no Character Summary block) */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Character Details</h3>
              {!isEditing ? (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><strong>Name:</strong> {charData.name}</div>
                    <div><strong>Age:</strong> {charData.age}</div>
                    <div><strong>Gender:</strong> {charData.gender}</div>
                    <div><strong>Role:</strong> {charData.role}</div>
                    {charData.countryOfOrigin && (
                      <div><strong>Origin:</strong> {charData.countryOfOrigin}</div>
                    )}
                    {charData.paintingStyle && (
                      <div><strong>Style:</strong> {charData.paintingStyle}</div>
                    )}
                    {charData.hairColor && (
                      <div><strong>Hair:</strong> {charData.hairStyle} {charData.hairColor}</div>
                    )}
                    {charData.eyeColor && (
                      <div><strong>Eyes:</strong> {charData.eyeColor}</div>
                    )}
                    {charData.skinTone && (
                      <div><strong>Skin:</strong> {charData.skinTone}</div>
                    )}
                    {charData.height && (
                      <div><strong>Height:</strong> {charData.height}</div>
                    )}
                    {charData.build && (
                      <div><strong>Build:</strong> {charData.build}</div>
                    )}
                  </div>
                  
                  {charData.appearance && (
                    <div>
                      <strong>Appearance:</strong>
                      <p className="text-sm mt-1">{charData.appearance}</p>
                    </div>
                  )}
                  
                  {charData.characterFace && (
                    <div>
                      <strong>Face Description:</strong>
                      <p className="text-sm mt-1">{charData.characterFace}</p>
                    </div>
                  )}
                  
                  {charData.clothing && (
                    <div>
                      <strong>Clothing:</strong>
                      <div className="text-sm mt-1">
                        {charData.clothing.top && <div>Top: {charData.clothing.top}</div>}
                        {charData.clothing.bottom && <div>Bottom: {charData.clothing.bottom}</div>}
                        {charData.clothing.colors && charData.clothing.colors.length > 0 && (
                          <div>Colors: {charData.clothing.colors.join(', ')}</div>
                        )}
                        {charData.clothing.accessories && charData.clothing.accessories.length > 0 && (
                          <div>Accessories: {charData.clothing.accessories.join(', ')}</div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {charData.distinctiveFeatures && charData.distinctiveFeatures.length > 0 && (
                    <div>
                      <strong>Distinctive Features:</strong>
                      <p className="text-sm mt-1">{charData.distinctiveFeatures.join(', ')}</p>
                    </div>
                  )}
                  
                  {charData.personality && (
                    <div>
                      <strong>Personality:</strong>
                      <p className="text-sm mt-1">{charData.personality}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Edit Prompt</label>
                  <textarea
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    placeholder="Describe the changes you want to make to the character..."
                    rows="4"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSaveEdit} className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-xl font-semibold flex items-center space-x-2">
                      <FaSave /><span>Save Changes</span>
                    </button>
                    <button onClick={() => setIsEditing(false)} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-xl font-semibold flex items-center space-x-2">
                      <FaTimes /><span>Cancel</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer — single set of actions (Edit, Regenerate, Cancel, Save) */}
        <div className="p-4 border-t border-gray-200 flex flex-wrap justify-between items-center gap-3 flex-shrink-0 bg-white">
          <div className="flex flex-wrap gap-2">
            <button onClick={handleEditClick} className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center space-x-2">
              <FaEdit /><span>Edit Character</span>
            </button>
            {handleRegenerate && (
              <button onClick={handleRegenerateClick} disabled={generating} className="bg-slate-500 hover:bg-slate-600 disabled:bg-gray-300 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center space-x-2">
                <FaRedo /><span>{generating ? 'Regenerating...' : 'Regenerate'}</span>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2.5 rounded-xl font-semibold">Cancel</button>
            <button onClick={handleSave} disabled={!charImage} className="bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center space-x-2">
              <FaCheckCircle /><span>Save Character</span>
            </button>
          </div>
        </div>
      </div>

      {/* Character Edit Modal — only mount when open to avoid undefined component in tree */}
      {showEditModal && CharacterEditModal && charData && (
        <CharacterEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          character={{
            ...charData,
            imageUrl: fullImageUrl
          }}
          onCharacterUpdate={handleCharacterUpdate}
        />
      )}
    </div>
  );
}

CharacterPreviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  character: PropTypes.object,
  characterImage: PropTypes.string,
  onSaveCharacter: PropTypes.func,
  onEditCharacter: PropTypes.func,
  onRegenerateImage: PropTypes.func,
  // New props for StorySetupModal integration
  characterData: PropTypes.object,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onRegenerate: PropTypes.func,
  isGenerating: PropTypes.bool,
  error: PropTypes.any
}; 